import { CdkMenuBar, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { booleanAttribute, ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { LucideChevronDown } from '@lucide/angular';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import { MmscanMenu, MmscanMenuItem } from '@mmscan/angular-ui/menu';
import type { MmscanNavItem } from './nav-item';

const ITEM_CLASS =
  'inline-flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-foreground outline-none hover:bg-accent focus-visible:bg-accent focus-visible:outline-2 focus-visible:outline-ring aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-[current=page]:text-primary-text aria-expanded:bg-accent';

/**
 * Horizontal application menu (`role="menubar"`, cdk menu): arrow keys move between items,
 * sub-menus open with ArrowDown/Enter. Links get `aria-current="page"` when active.
 * Replaces `p-menubar` with a `MenuItem[]` model.
 *
 * ```html
 * <mmscan-menubar [items]="navigation" ariaLabel="Main" />
 * ```
 */
@Component({
  selector: 'mmscan-menubar',
  imports: [
    CdkMenuBar,
    CdkMenuItem,
    CdkMenuTrigger,
    RouterLink,
    RouterLinkActive,
    MmscanIcon,
    MmscanMenu,
    MmscanMenuItem,
  ],
  template: `
    <nav [attr.aria-label]="ariaLabel()">
      <div cdkMenuBar class="flex flex-wrap items-center gap-1">
        @for (item of items(); track $index) {
          @if (item.children?.length) {
            <button
              cdkMenuItem
              type="button"
              [class]="itemClass"
              [cdkMenuTriggerFor]="submenu"
              [cdkMenuItemDisabled]="!!item.disabled"
            >
              @if (item.icon) {
                <mmscan-icon [icon]="item.icon" size="sm" [class.text-primary-text]="accentIcons()" />
              }
              {{ item.label }}
              <mmscan-icon [icon]="chevron" size="sm" class="text-muted-foreground" />
            </button>
            <ng-template #submenu>
              <mmscan-menu>
                @for (child of item.children; track $index) {
                  @if (child.route) {
                    <a
                      mmscanMenuItem
                      [routerLink]="child.route"
                      routerLinkActive
                      [routerLinkActiveOptions]="{ exact: !!child.exact }"
                      ariaCurrentWhenActive="page"
                      [disabled]="!!child.disabled"
                      (triggered)="child.command?.()"
                    >
                      @if (child.icon) {
                        <mmscan-icon [icon]="child.icon" size="sm" [class.text-primary-text]="accentIcons()" />
                      }
                      {{ child.label }}
                    </a>
                  } @else if (child.url) {
                    <a mmscanMenuItem [href]="child.url" [disabled]="!!child.disabled">{{ child.label }}</a>
                  } @else {
                    <button mmscanMenuItem type="button" [disabled]="!!child.disabled" (triggered)="child.command?.()">
                      @if (child.icon) {
                        <mmscan-icon [icon]="child.icon" size="sm" [class.text-primary-text]="accentIcons()" />
                      }
                      {{ child.label }}
                    </button>
                  }
                }
              </mmscan-menu>
            </ng-template>
          } @else if (item.route) {
            <a
              cdkMenuItem
              [class]="itemClass"
              [routerLink]="item.route"
              routerLinkActive
              [routerLinkActiveOptions]="{ exact: !!item.exact }"
              ariaCurrentWhenActive="page"
              [cdkMenuItemDisabled]="!!item.disabled"
            >
              @if (item.icon) {
                <mmscan-icon [icon]="item.icon" size="sm" [class.text-primary-text]="accentIcons()" />
              }
              {{ item.label }}
            </a>
          } @else if (item.url) {
            <a cdkMenuItem [class]="itemClass" [href]="item.url" [cdkMenuItemDisabled]="!!item.disabled">{{
              item.label
            }}</a>
          } @else {
            <button
              cdkMenuItem
              type="button"
              [class]="itemClass"
              [cdkMenuItemDisabled]="!!item.disabled"
              (cdkMenuItemTriggered)="item.command?.()"
            >
              @if (item.icon) {
                <mmscan-icon [icon]="item.icon" size="sm" [class.text-primary-text]="accentIcons()" />
              }
              {{ item.label }}
            </button>
          }
        }
      </div>
    </nav>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class MmscanMenubar {
  protected readonly itemClass = ITEM_CLASS;
  protected readonly chevron = LucideChevronDown;

  readonly items = input.required<readonly MmscanNavItem[]>();
  /** Name of the navigation landmark. */
  readonly ariaLabel = input<string>();
  /** Colour item icons with the primary colour. */
  readonly accentIcons = input(false, { transform: booleanAttribute });
}
